import { assertExhaustive } from 'backend-client-web';
import { CheckablePasswordPolicy, PasswordRules } from 'persistable-types';

export interface RuleResults {
  length: boolean;
  lower: boolean;
  upper: boolean | undefined;
  numbers: boolean | undefined;
  special: boolean | undefined;
}

export default function checkPasswordPolicy(
  password: string,
  policy: CheckablePasswordPolicy,
): {
  result: boolean;
  ruleResults: RuleResults;
  rules: PasswordRules;
} {
  const rules = getRulesForPasswordPolicy(policy);
  return checkPasswordRules(password, rules);
}

export function checkPasswordRules(password: string, rules: PasswordRules) {
  const ruleResults = {
    length: password.length >= rules.length,

    // For upper and lower case letters we accept a wide unicode range, to make sure passwords
    // containing umlauts or similar are also considered as containing the appropriate letter. Lower
    // case letters are always required regardless of policy.
    //
    // Note that since we've updated to TypeScript 5.5 we need to add ts-expect-error to be able to
    // use the v regex flag. TypeScript started to require an "esnext" target setting to allow use
    // of the flag. We don't want to set that target, because it will change as we update TypeScript
    // further. We can get rid of the ignore once a future TypeScript version adds the flag to a
    // stable target.
    //
    // @ts-expect-error -- Disabled due to target conflict, see comment above
    lower: !!password.match(/\p{Lowercase_Letter}/v),

    // @ts-expect-error -- Disabled due to target conflict, see comment above
    upper: rules.upper ? !!password.match(/\p{Uppercase_Letter}/v) : undefined,

    // There's a character class called "Decimal_Number" that we could use here. However, that would
    // probably not match user expectations. It would for example consider "٩٠٠١" as a password
    // containing numbers as it contains *arabic* numerals.
    numbers: rules.numbers ? !!password.match(/[0-9]/) : undefined,

    // @ts-expect-error -- Disabled due to target conflict, see comment above
    special: rules.special ? !!password.match(/\p{Punctuation}|\p{Symbol}/v) : undefined,
  };

  return {
    result: Object.values(ruleResults).every((res) => res !== false),
    ruleResults,
    rules,
  };
}

export function getRulesForPasswordPolicy(policy: CheckablePasswordPolicy): PasswordRules {
  switch (policy.type) {
    case 'custom':
      return policy.rules;
    default:
      assertExhaustive(policy.type);
  }
}
